import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
  keepPreviousData,
  useInfiniteQuery,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Booking,
  BookingCapabilities,
  BookingCompressed,
  BookingService,
  ISearchBookingsParameters,
  JxtBookingStatusEnum,
} from '@jooxter/api';
import { flatMap, uniqBy } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';
import { AxiosError } from 'axios';
import { BookingQueryKeys } from './queryKeys';
import { BookingStaleTimeEnum } from './staleTimes';

// Used as the default for `requiredCapabilities` below: a literal default parameter value would be
// re-created on every call where the caller omits the argument, defeating the `bookings` useMemo's
// memoization (new dependency reference every render, even when nothing actually changed).
const EMPTY_CAPABILITIES: (keyof BookingCapabilities)[] = [];

export const useSearchBookings = (
  options: ISearchBookingsParameters,
  enabled = true,
  requiredCapabilities: (keyof BookingCapabilities)[] = EMPTY_CAPABILITIES,
  fetchAll = false
): {
  bookings: BookingCompressed[] | undefined;
  isLoading: boolean;
  isError: boolean;
  fetchNextPage: (options?: FetchNextPageOptions | undefined) => Promise<
    InfiniteQueryObserverResult<
      InfiniteData<
        {
          data: BookingCompressed[];
          nextPage: string;
        },
        unknown
      >,
      Error
    >
  >;
  hasNextPage?: boolean;
  invalidateSearchBookings: () => void;
} => {
  const queryClient = useQueryClient();

  const invalidateSearchBookings = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [BookingQueryKeys.SearchBookings, options, requiredCapabilities],
    });
  }, [options, queryClient, requiredCapabilities]);

  if (!options.status || options.status?.length === 0) {
    options = {
      ...options,
      status: [JxtBookingStatusEnum.APPROVED, JxtBookingStatusEnum.REQUESTED, JxtBookingStatusEnum.FINISHED],
    };
  }

  const fetchBookings = useCallback(
    async ({ pageParam = '' }) => {
      const newOptions = { ...options };
      if (pageParam) {
        newOptions.page = pageParam;
      }

      return BookingService.search(newOptions);
    },
    [options]
  );

  const shouldFetch = enabled && options.from !== undefined && options.to !== undefined && options.from < options.to;

  const { data, isLoading, isError, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [BookingQueryKeys.SearchBookings, options, requiredCapabilities],
    queryFn: fetchBookings,
    getNextPageParam: (lastPage: { nextPage?: string; data: BookingCompressed[] }) => {
      // must return undefined if it's the last page
      // lastPage.nextPage is ''
      return lastPage.nextPage?.length && lastPage.nextPage.length > 0 ? lastPage.nextPage : undefined;
    },
    initialPageParam: '',
    refetchInterval: 30000,
    enabled: shouldFetch,
  });

  // Derived directly from `data` (rather than synced into its own state via an effect) so that
  // `bookings` is `undefined` for exactly as long as `data` is - i.e. it distinguishes "hasn't
  // fetched/resolved yet" from "fetched, zero results" on the very same render `isLoading` does,
  // instead of lagging one render cycle behind via a state+effect sync.
  const bookings = useMemo(() => {
    if (!data?.pages) {
      return undefined;
    }

    const flatBookings = uniqBy(flatMap(data.pages.map((page) => page.data)), 'id');

    if (requiredCapabilities.length === 0) {
      return flatBookings;
    }

    return flatBookings.filter((booking) =>
      requiredCapabilities.some((capability) => booking.capabilities?.[capability])
    );
  }, [data, requiredCapabilities]);

  useEffect(() => {
    if (data?.pages && hasNextPage && fetchAll) {
      fetchNextPage();
    }
  }, [data, hasNextPage, fetchNextPage, fetchAll]);

  return { bookings, isLoading, isError, fetchNextPage, hasNextPage, invalidateSearchBookings };
};

// Canonical version of this query - previously duplicated in
// packages/core/src/queries/queryFns/bookings.ts, which has been removed in favour of this one so
// both @jooxter/core and @jooxter/bookings consumers share it.
export const useFetchBooking = (
  id?: number,
  options?: { retry?: boolean }
): {
  booking: Booking | undefined | null;
  isLoading: boolean;
  isError: boolean;
  error: AxiosError<unknown> | null;
} => {
  const fetchBooking = useCallback(() => {
    if (id) {
      return BookingService.getById(id);
    }

    return Promise.resolve(null);
  }, [id]);

  const {
    data: booking,
    isLoading,
    isError,
    error,
  } = useQuery<Booking | null, AxiosError, Booking | null>({
    queryKey: [BookingQueryKeys.GetBooking, id, options?.retry],
    queryFn: () => fetchBooking(),
    placeholderData: keepPreviousData,
    staleTime: BookingStaleTimeEnum.GetBooking,
    retry: options?.retry,
    enabled: !!id,
  });

  return { booking, isLoading, isError, error };
};

export const useFetchBookings = (ids: number[]) => {
  const { data: bookings, isPending } = useQueries({
    queries: ids.map((id: number) => {
      return {
        queryKey: [BookingQueryKeys.GetBooking, id],
        queryFn: () => BookingService.getById(id),
        staleTime: BookingStaleTimeEnum.GetBooking,
      };
    }),
    combine: (results) => {
      return {
        data: results.map((result) => result.data).filter((r): r is Booking => !!r),
        isPending: results.some((result) => result.isPending),
      };
    },
  });

  return { bookings, isPending };
};
